// Operações sobre o array de campos (o layout).
//
// Todas são **puras e imutáveis**: recebem o layout, devolvem um novo. É o que
// permite desfazer/refazer no builder sem cópia defensiva espalhada.
//
// Toda operação que mexe na ESTRUTURA passa por `reindexSignatures`: o
// `index_signature` é a ligação do campo com o `<canvas>` no preenchimento
// (`signatureRef[index_signature]`), e o preenchimento numera os refs pela
// ordem de leitura do layout. Reordenar, duplicar ou apagar sem renumerar
// desalinha as assinaturas — armadilha conhecida do DSL (docs/04-melhorias.md).

// Assinaturas de topo e assinaturas dentro de tabelas têm pools separados
// (`signatureRef` e `signatureRefTable`), então são numeradas separadamente.
function reindexSignatures(fields) {
  let topLevel = 0;
  let inTables = 0;

  return (fields || []).map((field) => {
    if (field.type === "signature") {
      const next = { ...field, index_signature: topLevel };
      topLevel += 1;
      return next;
    }

    if (Array.isArray(field.columns)) {
      const columns = field.columns.map((column) => {
        if (column.type !== "signature") return column;
        const next = { ...column, index_signature: inTables };
        inTables += 1;
        return next;
      });
      return { ...field, columns };
    }

    return field;
  });
}

// `prefill_from.link_index` (product-table) aponta para um form-link pelo índice
// NESTE layout: inserir, mover ou apagar campos tem que levar o ponteiro junto.
// `newIndexOf(antigo)` devolve o novo índice, ou -1 se o campo saiu do layout —
// aí a ligação é desfeita (a chave some), em vez de apontar para outro campo.
function remapPrefillLinks(fields, newIndexOf) {
  return fields.map((field) => {
    if (!field.prefill_from) return field;
    const linkIndex = newIndexOf(Number(field.prefill_from.link_index));
    if (linkIndex === Number(field.prefill_from.link_index)) return field;

    const next = { ...field };
    if (linkIndex < 0) {
      delete next.prefill_from;
    } else {
      next.prefill_from = { ...field.prefill_from, link_index: linkIndex };
    }
    return next;
  });
}

function insertField(fields, field, index) {
  const next = [...fields];
  const at = index === undefined || index === null ? next.length : index;
  next.splice(at, 0, field);
  return reindexSignatures(remapPrefillLinks(next, (i) => (i >= at ? i + 1 : i)));
}

function removeField(fields, index) {
  return reindexSignatures(
    remapPrefillLinks(
      fields.filter((_, i) => i !== index),
      (i) => (i === index ? -1 : i > index ? i - 1 : i)
    )
  );
}

function duplicateField(fields, index) {
  const copy = JSON.parse(JSON.stringify(fields[index]));
  // O corpo é preenchimento, não layout: a cópia nasce vazia.
  if (Array.isArray(copy.body)) copy.body = [];
  return insertField(fields, copy, index + 1);
}

// Move o campo de `from` para `to` (índices do array já reordenado).
function moveField(fields, from, to) {
  if (from === to || to < 0 || to >= fields.length) return fields;
  const next = [...fields];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  // Mesma permutação aplicada aos índices, para saber onde cada um foi parar.
  const order = fields.map((_, i) => i);
  const [movedIndex] = order.splice(from, 1);
  order.splice(to, 0, movedIndex);
  return reindexSignatures(remapPrefillLinks(next, (i) => order.indexOf(i)));
}

// Aplica um patch no campo. Chave com valor `undefined` é **removida** — é como
// o inspetor apaga uma chave opcional (`identifier`, `hide_pdf`, `extra_fields`)
// em vez de deixar `false`/`[]` sujando o layout.
function updateField(fields, index, patch) {
  return fields.map((field, i) => {
    if (i !== index) return field;

    const next = { ...field, ...patch };
    Object.keys(patch).forEach((key) => {
      if (patch[key] === undefined) delete next[key];
    });
    return next;
  });
}

function updateFieldLabel(fields, index, label) {
  return fields.map((field, i) => {
    if (i !== index) return field;
    return { ...field, component: { ...(field.component || {}), label } };
  });
}

/* ----------------------------- colunas de tabela ---------------------------- */

function withColumns(fields, index, columns) {
  return reindexSignatures(
    fields.map((field, i) => (i === index ? { ...field, columns } : field))
  );
}

function insertColumn(fields, index, column) {
  const columns = [...(fields[index].columns || []), column];
  return withColumns(fields, index, columns);
}

function removeColumn(fields, index, columnIndex) {
  const columns = (fields[index].columns || []).filter((_, i) => i !== columnIndex);
  return withColumns(fields, index, columns);
}

function moveColumn(fields, index, from, to) {
  const columns = [...(fields[index].columns || [])];
  if (to < 0 || to >= columns.length) return fields;
  const [moved] = columns.splice(from, 1);
  columns.splice(to, 0, moved);
  return withColumns(fields, index, columns);
}

function duplicateColumn(fields, index, columnIndex) {
  const columns = [...(fields[index].columns || [])];
  const copy = JSON.parse(JSON.stringify(columns[columnIndex]));
  columns.splice(columnIndex + 1, 0, copy);
  return withColumns(fields, index, columns);
}

// Mesma semântica do `updateField`: `undefined` remove a chave.
function updateColumn(fields, index, columnIndex, patch) {
  const columns = (fields[index].columns || []).map((column, i) => {
    if (i !== columnIndex) return column;

    const next = { ...column, ...patch };
    Object.keys(patch).forEach((key) => {
      if (patch[key] === undefined) delete next[key];
    });
    return next;
  });

  return withColumns(fields, index, columns);
}

/* --------------------------------- leitura -------------------------------- */

// Índice do campo que define o identificador do preenchimento. Vence o último
// marcado (é a ordem em que o AddDialogForm sobrescreve a variável ao salvar),
// e é isso que o builder mostra para o usuário.
function identifierFieldIndex(fields) {
  let found = -1;
  (fields || []).forEach((field, index) => {
    if (field.identifier === true) found = index;
  });
  return found;
}

// Índice da coluna identificadora de uma tabela (vence a primeira marcada —
// é a que o AddDialogForm usa: `columns.filter(...)[0]`).
function identifierColumnIndex(field) {
  return (field.columns || []).findIndex((column) => column.identifier === true);
}

export {
  reindexSignatures,
  insertField,
  removeField,
  duplicateField,
  moveField,
  updateField,
  updateFieldLabel,
  insertColumn,
  removeColumn,
  moveColumn,
  duplicateColumn,
  updateColumn,
  identifierFieldIndex,
  identifierColumnIndex,
};
